import {
  required,
  alpha,
  alpha_dash,
  alpha_num,
  alpha_spaces,
  between,
  confirmed,
  digits,
  dimensions,
  email,
  ext,
  image,
  integer,
  is,
  is_not,
  length,
  max,
  max_value,
  mimes,
  min,
  min_value,
  not_one_of,
  numeric,
  one_of,
  regex,
  size,
  url,
} from '@vee-validate/rules';

import { maxFileSize } from './max-file-size';
import { maxLength } from './max-length';

import { localize, FieldValidationMetaInfo, setLocale } from '@vee-validate/i18n';
import en from '@vee-validate/i18n/dist/locale/en.json';
import sv from '@vee-validate/i18n/dist/locale/sv.json';
import fi from '@vee-validate/i18n/dist/locale/fi.json';

const getMessage = localize({
  en,
  sv,
  fi,
});

const validators = {
  required,
  alpha,
  alpha_dash,
  alpha_num,
  alpha_spaces,
  between,
  confirmed,
  digits,
  dimensions,
  email,
  ext,
  image,
  integer,
  is,
  is_not,
  length,
  max,
  max_value,
  mimes,
  min,
  min_value,
  not_one_of,
  numeric,
  one_of,
  regex,
  size,
  url,
};

const customValidators = {
  maxFileSize,
  maxLength,
};

type VeeValidators = typeof validators;
type CustomValidators = typeof customValidators;
type ValidatorKey = keyof (VeeValidators & CustomValidators);

type ValidatorShape = (arg1: any, arg2?: any) => any;

type Validator = (value: any) => boolean | string;

type VuetifyValidatorWithParams<Params> = (params: Params) => (value: any) => boolean | string | Promise<boolean | string>;

type VuetifyValidatorResult<T extends ValidatorShape> =
  Parameters<T> extends [any, any] ? VuetifyValidatorWithParams<Parameters<T>[1]> : Validator;

type Validators = {
  [key in ValidatorKey]: key extends keyof VeeValidators ? VuetifyValidatorResult<VeeValidators[key]> : Validator;
};

export type ValidatorFn = ((value: any) => boolean | string) & { fieldName: string };

function toVuetifyValidator<T extends ValidatorShape>(validator: T, validatorName: string) {
  function getValidatorResult<T>(params?: T) {
    return async function (value: any) {
      const validate = validator(value, params);

      return (
        (validate instanceof Promise ? await validate.then((res) => res) : validate) ||
        getMessage({ name: '', rule: { params, name: validatorName } } as FieldValidationMetaInfo)
      );
    };
  }

  // We check if validator expects second params which indicates it needs validator parameters
  const result = validator.length === 1 ? getValidatorResult() : (params: Parameters<T>[1]) => getValidatorResult(params);

  return result as VuetifyValidatorResult<typeof validator>;
}

export const hValidate = Object.keys(validators).reduce<Validators>(
  (prev, key) => ({
    ...prev,
    [key]: toVuetifyValidator(validators[key as ValidatorKey], key),
  }),
  { ...customValidators } as unknown as Validators,
);

export { setLocale as hSetValidatorsLocale };
